import type { Usage } from "./protocol";

/**
 * Presenting `RunState.usage` (contract §2.17, §12's `Usage`; handoff Tier 2 "usage meter").
 *
 * NO FIGURE HERE IS COMPUTED FROM ANOTHER. `cost_usd` is the backend's, `budget_pct` is the
 * backend's, and this module does not derive one from the other even when both are present —
 * frontend-rule §1: never fabricate money, usage or billing figures. If the server sends a cost and
 * no budget there is no percentage to show, and inventing one from a budget seen on a previous frame
 * would put a number on screen that no response contained.
 *
 * A MISSING FIELD IS NOT ZERO. `tokens_cached`, `budget_usd`, `budget_pct` and `hops` are all
 * optional. A reported `0` and an absent field must not render alike — 0 cached tokens is a
 * measurement, an absent `tokens_cached` means the backend did not report caching at all.
 */

export interface UsageLine {
  label: string;
  /** Formatted value, or `null` when the backend did not report it. */
  value: string | null;
}

export function formatTokens(n: number): string {
  if (n < 1000) return String(n);
  if (n < 1_000_000) return `${(n / 1000).toFixed(1)}k`;
  return `${(n / 1_000_000).toFixed(2)}M`;
}

/** `cost_usd` at cent precision, or sub-cent where that is all there is. */
export function formatCost(usd: number): string {
  if (usd === 0) return "$0.00";
  if (usd < 0.01) return `<$0.01`;
  return `$${usd.toFixed(2)}`;
}

export function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  const seconds = ms / 1000;
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  const minutes = Math.floor(seconds / 60);
  return `${minutes}m ${Math.round(seconds - minutes * 60)}s`;
}

/**
 * How the budget should read, if there is one.
 *
 * `state: "none"` is the common case and must not render an empty bar — a 0% bar next to "$0.00 of
 * $—" claims a budget exists and is untouched.
 *
 * THE THRESHOLD IS A DISPLAY CHOICE, NOT A RULE. Amber at 80% is this component deciding when to
 * draw attention; it is not a gate, and it must not be mistaken for one. The actual gate is the
 * backend's — an `approve_budget` interaction (§2.16) or a `BUDGET_EXCEEDED` error (§4.9) — and both
 * arrive as their own surfaces. Changing this number changes a colour and nothing else.
 */
export type BudgetState = { state: "none" } | { state: "ok" | "warn" | "over"; pct: number; budgetUsd: number };

const WARN_AT_PCT = 80;

export function budgetState(usage: Usage | null): BudgetState {
  if (!usage) return { state: "none" };
  const budgetUsd = typeof usage.budget_usd === "number" ? usage.budget_usd : null;
  const pct = typeof usage.budget_pct === "number" ? usage.budget_pct : null;
  // BOTH are required. A budget with no percentage cannot be drawn as a bar, and a percentage with
  // no budget is a proportion of an unknown — neither is worth a fabricated denominator.
  if (budgetUsd === null || pct === null) return { state: "none" };
  if (pct >= 100) return { state: "over", pct, budgetUsd };
  if (pct >= WARN_AT_PCT) return { state: "warn", pct, budgetUsd };
  return { state: "ok", pct, budgetUsd };
}

/**
 * The meter's detail rows, in a fixed order with absences preserved.
 *
 * `null` values render as an em dash rather than `0` — see this module's header. Callers may drop
 * the null rows for a compact view, but the decision is theirs and it is visible here.
 */
export function usageLines(usage: Usage | null): UsageLine[] {
  if (!usage) return [];
  const cached = typeof usage.tokens_cached === "number" ? usage.tokens_cached : null;
  const hops = typeof usage.hops === "number" ? usage.hops : null;
  return [
    { label: "Tokens in", value: formatTokens(usage.tokens_in) },
    { label: "Tokens out", value: formatTokens(usage.tokens_out) },
    // Absent means "caching was not reported", which is different from "nothing was cached".
    { label: "Cached", value: cached === null ? null : formatTokens(cached) },
    { label: "Tool calls", value: String(usage.tool_calls) },
    { label: "Hops", value: hops === null ? null : String(hops) },
    { label: "Elapsed", value: formatDuration(usage.duration_ms) },
  ];
}
